import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import HPButton from "@/components/common/Button";
import type { DatasetMetric } from "@models/Metric";
import type {
  ScorecardBrand,
  ScorecardDatasetMetricRow,
  ScorecardHeadingRow,
  ScorecardQuestionRow,
  ScorecardRow,
} from "@/models/Scorecard";
import { isDatasetMetricRow, isQuestionRow, isHeadingRow } from "@/models/Scorecard";
import { findDependentRows } from "@/utils/scorecard-row-lifecycle-utils";
import RowListItem from "./RowListItem";
import RowBuilder from "./RowBuilder";
import HeadingRowBuilder from "./HeadingRowBuilder";
import DependentRowsDeleteDialog from "./QuestionRowEditor/DependentRowsDeleteDialog";

interface RowsSectionProps {
  rows: ScorecardRow[];
  brands: ScorecardBrand[];
  datasetId: string | null;
  datasetMetrics: DatasetMetric[];
  onAddQuestionRow: (row: ScorecardQuestionRow) => void;
  onUpdateQuestionRow: (id: string, row: ScorecardQuestionRow) => void;
  onDuplicateQuestionRow: (id: string) => void;
  onRemoveQuestionRow: (id: string) => void;
  onAddDatasetMetricRow: (row: ScorecardDatasetMetricRow) => void;
  onUpdateDatasetMetricRow: (id: string, row: ScorecardDatasetMetricRow) => void;
  onDuplicateDatasetMetricRow: (id: string) => void;
  onRemoveDatasetMetricRow: (id: string) => void;
  onAddHeadingRow: (row: ScorecardHeadingRow) => void;
  onUpdateHeadingRow: (id: string, label: string) => void;
  onRemoveHeadingRow: (id: string) => void;
  onReorderRows: (orderedIds: string[]) => void;
}

type ActiveBuilder = "row" | "heading" | null;

const RowsSection: React.FC<RowsSectionProps> = ({
  rows,
  brands,
  datasetId,
  datasetMetrics,
  onAddQuestionRow,
  onUpdateQuestionRow,
  onDuplicateQuestionRow,
  onRemoveQuestionRow,
  onAddDatasetMetricRow,
  onUpdateDatasetMetricRow,
  onDuplicateDatasetMetricRow,
  onRemoveDatasetMetricRow,
  onAddHeadingRow,
  onUpdateHeadingRow,
  onRemoveHeadingRow,
  onReorderRows,
}) => {
  const [activeBuilder, setActiveBuilder] = useState<ActiveBuilder>(null);
  const [editingQuestionRow, setEditingQuestionRow] =
    useState<ScorecardQuestionRow | null>(null);
  const [editingDatasetMetricRow, setEditingDatasetMetricRow] =
    useState<ScorecardDatasetMetricRow | null>(null);
  const [editingHeadingRow, setEditingHeadingRow] =
    useState<ScorecardHeadingRow | null>(null);
  const [pendingDeleteRowId, setPendingDeleteRowId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const rowIds = rows.map((row) => row.id);
  const questionRows = rows.filter(isQuestionRow);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = rowIds.indexOf(active.id as string);
    const newIndex = rowIds.indexOf(over.id as string);
    if (oldIndex !== -1 && newIndex !== -1) {
      onReorderRows(arrayMove(rowIds, oldIndex, newIndex));
    }
  };

  const closeBuilder = () => {
    setActiveBuilder(null);
    setEditingQuestionRow(null);
    setEditingDatasetMetricRow(null);
    setEditingHeadingRow(null);
  };

  const handleSaveQuestionRow = (row: ScorecardQuestionRow) => {
    if (editingQuestionRow) onUpdateQuestionRow(editingQuestionRow.id, row);
    else onAddQuestionRow(row);
    closeBuilder();
  };

  const handleSaveDatasetMetricRow = (row: ScorecardDatasetMetricRow) => {
    if (editingDatasetMetricRow) onUpdateDatasetMetricRow(editingDatasetMetricRow.id, row);
    else onAddDatasetMetricRow(row);
    closeBuilder();
  };

  const handleSaveHeadingRow = (row: ScorecardHeadingRow) => {
    if (editingHeadingRow) onUpdateHeadingRow(editingHeadingRow.id, row.label);
    else onAddHeadingRow(row);
    closeBuilder();
  };

  const renderRowBuilder = () => (
    <RowBuilder
      datasetId={datasetId}
      brands={brands}
      otherQuestionRows={questionRows}
      datasetMetrics={datasetMetrics}
      editingQuestionRow={editingQuestionRow}
      editingDatasetMetricRow={editingDatasetMetricRow}
      onCancel={closeBuilder}
      onSaveQuestionRow={handleSaveQuestionRow}
      onSaveDatasetMetricRow={handleSaveDatasetMetricRow}
    />
  );

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Rows
      </Typography>

      {rows.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          At least one row is required.
        </Typography>
      )}

      {rows.length > 0 && (
        <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, mb: 1 }}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
              {rows.map((row) => (
                <React.Fragment key={row.id}>
                  <RowListItem
                    row={row}
                    brands={brands}
                    allRows={questionRows}
                    datasetMetrics={datasetMetrics}
                    onEdit={() => {
if (isQuestionRow(row)) {
  setEditingDatasetMetricRow(null);
  setEditingQuestionRow(row);
  setActiveBuilder("row");
} else if (isDatasetMetricRow(row)) {
  setEditingQuestionRow(null);
  setEditingDatasetMetricRow(row);
  setActiveBuilder("row");
} else if (isHeadingRow(row)) {
  setEditingQuestionRow(null);
  setEditingDatasetMetricRow(null);
  setEditingHeadingRow(row);
  setActiveBuilder("heading");
}
                    }}
                    onDuplicate={
                      isQuestionRow(row)
                        ? () => onDuplicateQuestionRow(row.id)
                        : isDatasetMetricRow(row)
                          ? () => onDuplicateDatasetMetricRow(row.id)
                          : undefined
                    }
                    onDelete={() => {
                      if (isHeadingRow(row)) {
                        onRemoveHeadingRow(row.id);
                        return;
                      }
                      if (isDatasetMetricRow(row)) {
                        onRemoveDatasetMetricRow(row.id);
                        return;
                      }
                      if (findDependentRows(rows, row.id).length > 0) {
                        setPendingDeleteRowId(row.id);
                      } else {
                        onRemoveQuestionRow(row.id);
                      }
                    }}
                  />
                  {/* Edit form opens right below the row it belongs to,
                      rather than always at the bottom of the list. */}
                  {activeBuilder === "row" &&
                    (editingQuestionRow?.id === row.id ||
                      editingDatasetMetricRow?.id === row.id) &&
                    renderRowBuilder()}
                  {activeBuilder === "heading" && editingHeadingRow?.id === row.id && (
                    <HeadingRowBuilder
                      editingRow={editingHeadingRow}
                      onCancel={closeBuilder}
                      onSave={handleSaveHeadingRow}
                    />
                  )}
                </React.Fragment>
              ))}
            </SortableContext>
          </DndContext>
        </Box>
      )}

      {!activeBuilder && (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <HPButton
              variant="outlined"
              size="small"
              className="w-full"
              onClick={() => {
                setEditingQuestionRow(null);
                setEditingDatasetMetricRow(null);
                setActiveBuilder("row");
              }}
            >
              Add Row
            </HPButton>
          </Box>
          <Box sx={{ flex: 1 }}>
            <HPButton
              variant="outlined"
              size="small"
              className="w-full"
              onClick={() => {
                setEditingHeadingRow(null);
                setActiveBuilder("heading");
              }}
            >
              Add Heading Row
            </HPButton>
          </Box>
        </Box>
      )}

      {/* A brand-new row (not editing an existing one) has no row position to
          attach to, so its builder stays here, below the list. Edits render
          inline next to their own row instead — see the rows.map loop above. */}
      {activeBuilder === "row" && !editingQuestionRow && !editingDatasetMetricRow && renderRowBuilder()}

      {activeBuilder === "heading" && !editingHeadingRow && (
        <HeadingRowBuilder
          editingRow={null}
          onCancel={closeBuilder}
          onSave={handleSaveHeadingRow}
        />
      )}

      <DependentRowsDeleteDialog
        open={!!pendingDeleteRowId}
        rowLabel={questionRows.find((r) => r.id === pendingDeleteRowId)?.label ?? ""}
        dependentRows={
          pendingDeleteRowId ? findDependentRows(rows, pendingDeleteRowId) : []
        }
        onCancel={() => setPendingDeleteRowId(null)}
        onConfirm={() => {
          if (pendingDeleteRowId) onRemoveQuestionRow(pendingDeleteRowId);
          setPendingDeleteRowId(null);
        }}
      />
    </Box>
  );
};

export default RowsSection;
